import { isEqual } from '../object-utils';
import { isNgram } from '../../typeguards';
import {
  isSingleSelection,
  isMultipleSelection,
  getFeature,
  getFeatureForSelection,
  isDisplayedFeature,
  buildEvidenceFeatures,
  hasSentence
} from './feature-utils';
import { getCode } from './lookup';
import { getFieldId } from './utils';
import { loggerFactory } from '../logger';

const logger = loggerFactory('EvidenceManager');
// ---------------------------------
// Type Definitions
// ---------------------------------
type FeatureMap = {
  [key: string]: CMxArtifacts.FeatureSelection[];
};
type SpanKey = keyof CMxAPI.Span;
// ---------------------------------
// Utility functions
// ---------------------------------

const identifyFeatureAttr = (feature: CMxAPI.Feature) => {
  // this is not going to end up being good enough.
  // we should really resolve whether there's a better
  // identifying feature on a feature.
  if ('field' in feature) {
    return `${getFieldId(feature.field)}`;
  }
  // we should do something better here
  return JSON.stringify(feature);
};

const hashSelection = (featureSelection: CMxArtifacts.FeatureSelection) => {
  const { artifactId, code } = featureSelection;
  const codeId = getCode(code);

  if (!isSingleSelection(featureSelection)) {
    return `${artifactId}-${codeId}`;
  } else {
    const feature = getFeatureForSelection(featureSelection);
    return !feature
      ? `${artifactId}-${code.uuid}`
      : `${artifactId}-${code.uuid}-${identifyFeatureAttr(feature)}`;
  }
};

// ---------------------------------
// Private class to handle highlight for an individual
// field.  Current behavior means that prior to a field
// indicating a selection, it first has to have a background
// highlight.
// ---------------------------------
class EvidenceHighlighting {
  private _background: FeatureMap;
  private _foreground: FeatureMap;

  constructor() {
    this._background = {};
    this._foreground = {};
  }

  clone(): EvidenceHighlighting {
    const cloned = new EvidenceHighlighting();
    cloned._background = { ...this.highlights };
    cloned._foreground = { ...this.selections };
    return cloned;
  }

  get selections() {
    return this._foreground;
  }
  get highlights() {
    return this._background;
  }

  hasSelection(optFeature?: CMxArtifacts.FeatureSelection) {
    if (optFeature === undefined) {
      return Object.keys(this._foreground).length !== 0;
    } else {
      const hash = hashSelection(optFeature);
      return this._foreground[hash] !== undefined;
    }
  }

  hasHighlight(optFeature?: CMxArtifacts.FeatureSelection) {
    if (optFeature === undefined) {
      return Object.keys(this._background).length !== 0;
    } else {
      const hash = hashSelection(optFeature);
      return this._background[hash] !== undefined;
    }
  }
  private static addToMap(
    selection: CMxArtifacts.FeatureSelection,
    mapper: FeatureMap
  ) {
    // adds a feature to a feature map if the feature has not already been added
    const hash = hashSelection(selection);
    if (!Array.isArray(mapper[hash])) {
      mapper[hash] = [];
    }
    const list = mapper[hash];

    const contains = list.some(
      member =>
        member.artifactId === selection.artifactId &&
        member.code.uuid === selection.code.uuid
    );
    if (!contains) {
      list.push(selection);
    }
  }

  addHighlight(selection: CMxArtifacts.FeatureSelection) {
    EvidenceHighlighting.addToMap(selection, this._background);
  }

  removeHighlight(selection: CMxArtifacts.FeatureSelection) {
    const toRmHash = hashSelection(selection);
    delete this._background[toRmHash];
    this.removeSelection(selection);
  }

  addSelection(selection: CMxArtifacts.FeatureSelection) {
    EvidenceHighlighting.addToMap(selection, this._foreground);
  }

  removeSelection(selection: CMxArtifacts.FeatureSelection) {
    const toRmHash = hashSelection(selection);
    delete this._foreground[toRmHash];
  }
}

type FeatureSetMap = {
  [key: string]: Set<CMxArtifacts.FeatureSelection>;
};
class CodeActivations {
  private _codeMaps: FeatureSetMap;

  constructor() {
    this._codeMaps = {};
  }

  private static hash(featureSelection: CMxArtifacts.FeatureSelection) {
    const { code } = featureSelection;
    return `${code.uuid}`;
  }

  private _compareFeatures(featureA: any, featureB: any) {
    const {
      evidence: { feature: featA }
    } = featureA;
    const {
      evidence: { feature: featB }
    } = featureB;

    return isEqual(featA, featB);
  }

  hasFeature(uniqueFeature: CMxArtifacts.FeatureSelection) {
    const hash = CodeActivations.hash(uniqueFeature);
    const activeSet = this._codeMaps[hash];

    if (!activeSet) {
      return false;
    }
    //@ts-ignore
    const lst = [...activeSet.values()];
    return (
      lst.findIndex(feature =>
        this._compareFeatures(feature, uniqueFeature)
      ) !== -1
    );
  }

  clone() {
    const cloned = new CodeActivations();
    cloned._codeMaps = { ...this._codeMaps };
    return cloned;
  }

  getSelectionSpans(fieldId: string, documentId?: number) {
    const selectionSpans: CMxAPI.Span[] = [];

    Object.values(this._codeMaps).forEach(entry => {
      Array.from(entry).forEach(featureSelection => {
        const feature = getFeatureForSelection(featureSelection);

        if (isDisplayedFeature(feature) && feature.field === fieldId) {
          if (isNgram(feature)) {
            const spans = feature.spans ?? [];
            spans.forEach((span: CMxAPI.Span) => {
              if (documentId === undefined) {
                selectionSpans.push(span);
              } else if (span.document_id === documentId) {
                selectionSpans.push(span);
              }
            });
          }
        }
      });
    });
    return selectionSpans;
  }

  addSelection(selection: CMxArtifacts.FeatureSelection) {
    const hash = CodeActivations.hash(selection);
    if (!this._codeMaps[hash]) {
      this._codeMaps[hash] = new Set();
    }
    const activationSet = this._codeMaps[hash];
    activationSet.add(selection);
  }

  removeSelection(selection: CMxArtifacts.FeatureSelection) {
    const hash = CodeActivations.hash(selection);
    if (this._codeMaps[hash]) {
      const hashSet = this._codeMaps[hash];
      const lst = [...hashSet.values()];
      const featureB = selection as CMxArtifacts.SingleSelection;
      const filtered = lst.filter(feature => {
        return !this._compareFeatures(feature, featureB);
      });
      this._codeMaps[hash] = new Set(filtered);
    } else {
      delete this._codeMaps[hash];
    }
  }
}

// ---------------------------------
// Public API
// ---------------------------------
export class EvidenceManager implements CMxCommonApp.EvidenceManager {
  protected _evidenceMappers: Map<string, EvidenceHighlighting>;
  protected _codeActivations: CodeActivations;
  protected _evidence?: CMxAPI.MLPredictions[];
  protected _artifacts: CMxArtifacts.EnhancedCaseArtifacts[];

  constructor(artifacts: CMxArtifacts.EnhancedCaseArtifacts[] = []) {
    this._evidenceMappers = new Map();
    this._codeActivations = new CodeActivations();
    this._artifacts = artifacts;
  }

  public clone() {
    const cloned = new EvidenceManager();
    //@ts-ignore
    [...this._evidenceMappers.keys()].forEach(fieldId => {
      const field = this._evidenceMappers.get(fieldId);
      if (!field) {
        console.log(`no field available`);
      } else {
        cloned._evidenceMappers.set(fieldId, field.clone());
      }
    });
    cloned._codeActivations = this._codeActivations.clone();
    return cloned;
  }

  set artifacts(arts) {
    this._artifacts = arts;
  }

  get artifacts() {
    return this._artifacts;
  }

  get evidence() {
    if (!this._evidence) return [];
    return this._evidence;
  }

  get artifactList() {
    return this.artifacts;
  }

  public getHighlights(fieldName: string): CMxArtifacts.FeatureSelection[] {
    const fieldId = getFieldId(fieldName);
    let mapper = this._evidenceMappers.get(fieldId);
    if (mapper === undefined) {
      return [];
    }
    return [...Object.values(mapper.highlights)].flat();
  }

  public getHighlightSpans(
    fieldName: string,
    matchableMetadata: any = {}
  ): CMxArtifacts.UnstructuredSpan[] {
    const highlightSpans: CMxArtifacts.UnstructuredSpan[] = [];

    const fieldId = getFieldId(fieldName);
    let highlightedFeatures = this.getHighlights(fieldId);

    if (highlightedFeatures) {
      // ------------------------ closures and data structures ------------------------ //
      // build a matcher to make sure evidence highlights points to current document/etc.
      const keys = Object.keys(matchableMetadata);
      const metadataMatcher = (span: CMxAPI.Span) => {
        return !keys.some(
          // eslint-disable-next-line
          key => span[key as SpanKey] != matchableMetadata[key]
        );
      };

      // build a set of activations
      const activationSpans = this._codeActivations.getSelectionSpans(fieldId);
      const activationSet = new Set(
        activationSpans.map(activation => `${activation.hash}`)
      );

      highlightedFeatures.forEach(result => {
        const evidence = result.code.evidence ?? [];
        evidence.forEach(pieceOfEvidence => {
          const feature = getFeature(pieceOfEvidence);
          if (isDisplayedFeature(feature) && isNgram(feature)) {
            const sentenceSpans = (feature.spans ?? []).filter(hasSentence);
            const matchedSpans = sentenceSpans.filter(metadataMatcher);

            matchedSpans.forEach(span => {
              highlightSpans.push({
                span,
                codeType: result.codeType,
                code: result.code,
                active: activationSet.has(span.hash ?? '')
              });
            });
          }
        });
      });
    }

    return highlightSpans;
  }

  public getAllHighlights() {
    const highlights = [...this._evidenceMappers.keys()]
      .map(key => {
        return this.getHighlights(key);
      })
      .flat()
      .map(highlight => {
        // when multiple-selections are added to the evidence manager, they
        // are added as individual selections.  What this means is that when
        // you go to grab highlights, they can't simply be re-added as is.
        // the code below is converting the single selection to the multiple.
        return {
          ...highlight,
          evidence: [highlight.evidence]
        };
      }) as CMxArtifacts.FeatureSelection[];
    return highlights;
  }

  public getAllSelections() {
    return [...this._evidenceMappers.keys()]
      .map(key => {
        return this.getSelections(key);
      })
      .flat();
  }

  public getSelections(fieldName: string): CMxArtifacts.FeatureSelection[] {
    const fieldId = getFieldId(fieldName);
    let evidence = this._evidenceMappers.get(fieldId);
    if (evidence === undefined) {
      return [];
    }
    return [...Object.values(evidence.selections)].flat();
  }

  public isSelected(fieldName: string) {
    const fieldId = getFieldId(fieldName);
    let evidence = this._evidenceMappers.get(fieldId);
    return evidence !== undefined && evidence.hasSelection();
  }

  public isHighlighted(fieldName: string) {
    const fieldId = getFieldId(fieldName);
    let mapper = this._evidenceMappers.get(fieldId);
    return mapper !== undefined && mapper.hasHighlight();
  }

  public loadEvidence(caseDetail: CMxAPI.CaseRun) {
    const featureSelections = buildEvidenceFeatures(caseDetail);

    featureSelections.forEach(featureSelection => {
      this.highlightFeatures(featureSelection);
    });
  }

  public deselectFeature(toRemove: CMxArtifacts.FeatureSelection) {
    if (isSingleSelection(toRemove)) {
      const feature = getFeatureForSelection(toRemove);
      const fieldId = identifyFeatureAttr(feature);
      let mapper = this._evidenceMappers.get(fieldId);
      if (!mapper) {
        console.log(
          `issues with removal, unable to locate evidence for ${toRemove}`
        );
      } else {
        mapper.removeSelection(toRemove);
        this._codeActivations.removeSelection(toRemove);
      }
    }
  }

  public selectFeature(toSelect: CMxArtifacts.FeatureSelection) {
    if (isSingleSelection(toSelect)) {
      const feature = getFeatureForSelection(toSelect);
      const fieldId = identifyFeatureAttr(feature);
      let mapper = this._evidenceMappers.get(fieldId);

      if (!mapper) {
        // create the evidence structure if it isn't there already
        mapper = new EvidenceHighlighting();
        this._evidenceMappers.set(fieldId, mapper);
      }
      mapper.addSelection(toSelect);
      this._codeActivations.addSelection(toSelect);
    }
  }

  public hasSelectedFeature(uniqueFeature: CMxArtifacts.FeatureSelection) {
    return this._codeActivations.hasFeature(uniqueFeature);
  }

  public highlightFeatures(toHighlight: CMxArtifacts.FeatureSelection) {
    if (isMultipleSelection(toHighlight)) {
      const { evidence, ...base } = toHighlight;
      evidence.forEach(pieceOfEvidence => {
        const feature = getFeature(pieceOfEvidence);
        // skipping priors for the moment.
        if (isDisplayedFeature(feature)) {
          const fieldId = identifyFeatureAttr(feature);
          let mapper = this._evidenceMappers.get(fieldId);

          if (!mapper) {
            // create the evidence structure if it isn't there already
            mapper = new EvidenceHighlighting();
            this._evidenceMappers.set(fieldId, mapper);
          }
          const item = { ...base, evidence: { ...pieceOfEvidence } };
          mapper.addHighlight(item);
        }
      });
    }
  }

  public deHighlightFeatures(rmHighlight: CMxArtifacts.FeatureSelection) {
    if (isMultipleSelection(rmHighlight)) {
      const { evidence, ...base } = rmHighlight;

      evidence.forEach(pieceOfEvidence => {
        const feature = getFeature(pieceOfEvidence);
        if (isDisplayedFeature(feature)) {
          const fieldId = identifyFeatureAttr(feature);
          let mapper = this._evidenceMappers.get(fieldId);

          if (!mapper) {
            logger.log(
              `unable to de highlight the feature ${fieldId}`,
              pieceOfEvidence,
              fieldId
            );
          } else {
            const item = { ...base, evidence: { ...pieceOfEvidence } };
            mapper.removeHighlight(item);

            this._codeActivations.removeSelection(item);
          }
        }
      });
    }
  }
}
